import { World } from './world'
import { baseStats, Stats } from './types'
import { clamp } from '../core/math'
import { CHARACTERS } from '../data/characters'
import { ITEMS } from '../data/items'
import { TOMES } from '../data/tomes'
import { CHALLENGES } from '../data/challenges'

/** Permanent meta-progression bonuses applied on top of base stats. */
export interface MetaBonuses {
  maxHp: number
  damage: number
  moveSpeed: number
  attackSpeed: number
  armor: number
  luck: number
  xpGain: number
  goldGain: number
  pickupRange: number
  critChance: number
  revives: number
  // PT4 — additional meta levers
  critMult: number
  area: number
  projectileSpeed: number
  hpRegen: number
  lifesteal: number
  projectileCount: number
}

export function emptyMeta(): MetaBonuses {
  return {
    maxHp: 0,
    damage: 0,
    moveSpeed: 0,
    attackSpeed: 0,
    armor: 0,
    luck: 0,
    xpGain: 0,
    goldGain: 0,
    pickupRange: 0,
    critChance: 0,
    revives: 0,
    critMult: 0,
    area: 0,
    projectileSpeed: 0,
    hpRegen: 0,
    lifesteal: 0,
    projectileCount: 0,
  }
}

function applyMeta(s: Stats, m: MetaBonuses): void {
  s.maxHp += m.maxHp
  s.damage += m.damage
  s.moveSpeed *= 1 + m.moveSpeed
  s.attackSpeed += m.attackSpeed
  s.armor += m.armor
  s.luck += m.luck
  s.xpGain *= 1 + m.xpGain
  s.goldGain *= 1 + m.goldGain
  s.pickupRange *= 1 + m.pickupRange
  s.critChance += m.critChance
  s.revives += m.revives
  // PT4
  s.critMult += m.critMult
  s.area *= 1 + m.area
  s.projectileSpeed *= 1 + m.projectileSpeed
  s.hpRegen += m.hpRegen
  s.lifesteal += m.lifesteal
  s.projectileCount += m.projectileCount
}

/**
 * Recompute the player's full stat block. Preserves HP ratio, and when max HP
 * grows (e.g. from a +HP upgrade) heals the player by the gained amount.
 */
export function recomputeStats(world: World, meta?: MetaBonuses): void {
  const prevMax = world.stats.maxHp
  const prevHp = world.player.hp

  const s = baseStats()
  const char = CHARACTERS[world.characterId]
  if (char) {
    char.applyBase(s)
    char.perLevel?.(s, world.player.level) // per-level scaling identity (Megabonk-style) — re-evaluated each recompute
  }
  world.marksman = char?.passive === 'marksman' // Marksman hero: weapons fire where you aim (cone toward facing)
  // 'dice' hero (Roll): apply the accumulated random level-up surges (see checkLevelUp). Additive, deterministic.
  const ds = world.diceStats
  s.damage += ds.damage
  s.attackSpeed += ds.attackSpeed
  s.critChance += ds.critChance
  s.critMult += ds.critMult
  s.maxHp += ds.maxHp
  s.armor += ds.armor
  if (meta) applyMeta(s, meta)
  world.procs = []
  for (const it of world.items) {
    const def = ITEMS[it.defId]
    if (def) def.apply(s, it.level)
    if (def?.proc) world.procs.push({ hook: def.proc.hook, kind: def.proc.kind, level: it.level })
  }
  // MP4: the Frozen Heart catalyst turns a freeze build into a cryo build (shatter + execute)
  world.frostReactions = !!world.hasItem('frozen_heart')
  // PT1: Ember Core → burning deaths combust (fire nova); Fungal Spores → poisoned deaths spread contagion
  world.combustReactions = !!world.hasItem('ember_core')
  world.contagionReactions = !!world.hasItem('fungal_spores')
  world.conductReactions = !!world.hasItem('capacitor') // PT1b: crits arc bonus lightning
  for (const tm of world.tomes) {
    const def = TOMES[tm.defId]
    if (def) def.apply(s, tm.level)
  }
  if (world.challenge) CHALLENGES[world.challenge]?.applyStats?.(s)

  // cooldown reduction stacks into the global attack-speed multiplier
  if (s.cooldownReduction > 0) {
    s.attackSpeed += Math.min(0.8, s.cooldownReduction)
    s.cooldownReduction = 0
  }

  // clamps / sanity
  s.maxHp = Math.max(1, s.maxHp)
  s.critChance = clamp(s.critChance, 0, 1)
  s.dodge = clamp(s.dodge, 0, 0.75)
  s.attackSpeed = Math.max(0.1, s.attackSpeed)
  s.moveSpeed = Math.max(0.5, s.moveSpeed)
  s.lifesteal = Math.max(0, s.lifesteal)

  world.stats = s

  // preserve / grow HP
  const gained = s.maxHp - prevMax
  if (gained > 0) world.player.hp = Math.min(s.maxHp, prevHp + gained)
  else world.player.hp = Math.min(s.maxHp, prevHp)
}

/** Flat-armor damage reduction with diminishing returns. Returns final damage. */
export function applyArmor(raw: number, armor: number): number {
  if (armor <= 0) return raw
  const reduction = armor / (armor + 28)
  return raw * (1 - reduction)
}
